import { AlertListTypeEnum, AlertTypesEnum } from '../../../../utilities/Constants';
import { IAlertSummaryModel, IAlertUserUpdateModel } from '../../../../interfaces/INote';
import { Placeholder, Stack } from 'react-bootstrap';

import Authorization from '../../../../stores/Authorization';
import CheckmateApiClient from '../../../../services/apiClient';
import { HubConnection } from '@microsoft/signalr';
import { LocalRoutes } from '../../../../utilities/LocalRoutes';
import React from 'react';
import { SignalRMethods } from '../../../../utilities/SignalR';
import { cloneDeep } from 'lodash';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useGetAlertSummary } from './useGetAlertSummary';
import { useQueryClient } from 'react-query';
import { useZoneContext } from '../../../../contexts/useZoneContext';

const _apiClient = new CheckmateApiClient();

interface IUserAlertListProps {
    alertHubConnection: HubConnection | undefined;
    showReportCounts?: boolean;
}

export function UserAlertList(props: IUserAlertListProps) {
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();
    const queryClient = useQueryClient();
    const alertSummaryApi = useGetAlertSummary();

    const userHasTriggerRight = Authorization.userHasAtleastOneTriggerRight(
        user ?? { active: false }
    );

    const goToAlertList = async (organizationGuid: string, organizationName: string) => {
        const alertListUrl = LocalRoutes.Alerts.replace(
            ':type',
            AlertListTypeEnum.MyAlertList.toString()
        );

        if (zoneContext.zoneGuid === organizationGuid) {
            zoneContext.zoneNavigate(alertListUrl);
        } else {
            await zoneContext.zoneChange(organizationGuid, organizationName, alertListUrl);
        }
    };

    const onlySummariesWithDataFilter = (alert: IAlertSummaryModel) => {
        return (
            alert.actCount > 0 ||
            alert.reportCount > 0 ||
            alert.triggerCount > 0 ||
            alert.watchCount > 0
        );
    };

    const handleSendAlertNotificationUpdates = (payload: IAlertUserUpdateModel) => {
        const userAlertSummaryCopy = cloneDeep(alertSummaryApi.data ?? []);

        for (let index = 0; index < payload.alertUpdates.length; index++) {
            const orgUpdateElement = payload.alertUpdates[index];

            let matchInUserAlertSummary = userAlertSummaryCopy.find(
                (x) => x.organization.guid === orgUpdateElement.organization.guid
            );

            if (!matchInUserAlertSummary) {
                matchInUserAlertSummary = {
                    organization: orgUpdateElement.organization,
                    actCount: 0,
                    reportCount: 0,
                    triggerCount: 0,
                    watchCount: 0,
                };
                userAlertSummaryCopy.push(matchInUserAlertSummary);
            }

            if (matchInUserAlertSummary) {
                for (let index = 0; index < orgUpdateElement.changeByType.length; index++) {
                    const changeByTypeElement = orgUpdateElement.changeByType[index];
                    switch (changeByTypeElement.type) {
                        case AlertTypesEnum.Act:
                            matchInUserAlertSummary.actCount += changeByTypeElement.change;
                            break;
                        case AlertTypesEnum.Watch:
                            matchInUserAlertSummary.watchCount += changeByTypeElement.change;
                            if (matchInUserAlertSummary.watchCount < 0)
                                matchInUserAlertSummary.watchCount = 0;
                            break;
                        case AlertTypesEnum.CaseTrigger:
                            matchInUserAlertSummary.triggerCount += changeByTypeElement.change;
                            if (matchInUserAlertSummary.triggerCount < 0)
                                matchInUserAlertSummary.triggerCount = 0;
                            break;
                        case AlertTypesEnum.Report:
                            matchInUserAlertSummary.reportCount += changeByTypeElement.change;
                            if (matchInUserAlertSummary.reportCount < 0)
                                matchInUserAlertSummary.reportCount = 0;
                            break;
                        default:
                            break;
                    }
                }
            }
        }

        queryClient.setQueryData(['get-alert-summary'], () => {
            return userAlertSummaryCopy.filter(onlySummariesWithDataFilter);
        });
    };

    React.useEffect(() => {
        props.alertHubConnection?.on(
            SignalRMethods.SendAlertNotificationUpdates,
            handleSendAlertNotificationUpdates
        );
    }, [alertSummaryApi.data]);

    let columnCount = 3;

    if (userHasTriggerRight) columnCount++;
    if (props.showReportCounts) columnCount++;

    return (
        <div style={{ minWidth: '100%' }}>
            <div className="horizontal-padding-only">
                <span className="bold uppercase">Alerts</span>
            </div>
            <hr
                style={{
                    margin: '5px auto',
                    opacity: 'inherit',
                }}
            />
            <table className="alert-summary-table" style={{ minWidth: '100%' }}>
                <thead>
                    <tr>
                        <th className="color-yellow bold uppercase col-sm-4 padding-left-5 font-size-xs">
                            Zone
                        </th>
                        {userHasTriggerRight && (
                            <th className="text-center color-orange bold col-sm-2 font-size-xs">
                                <i className="fal fa-chevrons-right" />
                            </th>
                        )}
                        {props.showReportCounts && (
                            <th className="text-center color-yellow bold col-sm-2 font-size-xs">
                                <i className="fal fa-file-chart-column" />
                            </th>
                        )}
                        <th className="text-center color-yellow bold col-sm-2 font-size-xs">ACT</th>
                        <th className="text-center color-yellow bold col-sm-2 font-size-xs">
                            <i className="fal fa-glasses-alt" />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {alertSummaryApi.data?.map((item: IAlertSummaryModel, index: number) => {
                        return (
                            <tr
                                key={index}
                                onClick={() => {
                                    goToAlertList(
                                        item.organization.guid!,
                                        item.organization!.name!
                                    );
                                }}
                            >
                                <td>
                                    <span className="font-size-xs padding-left-5">
                                        {item.organization?.shortName ?? item.organization?.name}
                                    </span>
                                </td>
                                {userHasTriggerRight && (
                                    <td className="text-center col-sm-2 font-size-xs">
                                        {item.triggerCount > 0 && item.triggerCount}
                                    </td>
                                )}
                                {props.showReportCounts && (
                                    <td className="text-center col-sm-2 font-size-xs">
                                        {item.reportCount > 0 && item.reportCount}
                                    </td>
                                )}
                                <td className="text-center col-sm-2 font-size-xs">
                                    {item.actCount > 0 && item.actCount}
                                </td>
                                <td className="text-center col-sm-2 font-size-xs">
                                    {item.watchCount > 0 && item.watchCount}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {alertSummaryApi.isFetching && (
                <Stack style={{ gap: 5, marginTop: 8 }}>
                    <PlaceholderRow columnCount={columnCount} />
                    <PlaceholderRow columnCount={columnCount} />
                    <PlaceholderRow columnCount={columnCount} />
                </Stack>
            )}
        </div>
    );
}

function PlaceholderRow(props: { columnCount?: number }) {
    const width = props.columnCount === 5 ? 38 : 45;
    const colCount = props.columnCount ?? 1;

    const sharedStyle: React.CSSProperties = {
        borderRadius: 3,
        opacity: 0.25,
        height: 18,
        animationDelay: '0s',
        animationDuration: '2s',
    };

    return (
        <Placeholder as="div" animation="glow" className="d-flex" style={{ gap: 5 }}>
            <Placeholder bg="secondary" style={{ ...sharedStyle, flexGrow: 1 }} />
            {colCount > 1 &&
                Array.from({ length: colCount - 1 }).map((_, i) => (
                    <Placeholder
                        key={i}
                        bg="secondary"
                        style={{ ...sharedStyle, width, flexShrink: 0 }}
                    />
                ))}
        </Placeholder>
    );
}
